import { useNavigate } from "react-router-dom";
import {
  Sun,
  Moon,
  ArrowLeft,
  Info,
  Trash2,
  Type,
  ALargeSmall,
  ScrollText,
  GalleryHorizontalEnd,
  RotateCcw,
} from "lucide-react";
import Header from "../components/Header";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";

export default function Settings() {
  const {
    theme,
    setTheme,
    fontSize,
    setFontSize,
    formLayout,
    setFormLayout,
    logout,
    resetSetup,
  } = useApp();
  const navigate = useNavigate();

  const handleLogout = () => {
    if (window.confirm("¿Cerrar sesión?")) {
      logout();
      navigate("/login", { replace: true });
    }
  };

  const replayWizard = () => {
    resetSetup();
    navigate("/", { replace: true });
  };

  const Option = ({ active, onClick, icon: Icon, title, description, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 p-4 rounded-md border transition-colors text-left ${
        active
          ? "border-orange-500 bg-orange-500/5 dark:bg-orange-500/10"
          : "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-zinc-300 dark:hover:border-zinc-700"
      }`}
    >
      <div
        className={`w-10 h-10 rounded-sm flex items-center justify-center shrink-0 ${
          active
            ? "bg-orange-500 text-white"
            : "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
        }`}
      >
        <Icon className="w-5 h-5" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold text-sm uppercase tracking-wider text-zinc-900 dark:text-white">
          {title}
        </p>
        <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
          {description}
        </p>
      </div>
      <div
        className={`w-4 h-4 rounded-full border-2 shrink-0 transition-colors ${
          active
            ? "border-orange-500 bg-orange-500"
            : "border-zinc-300 dark:border-zinc-700"
        }`}
      />
    </button>
  );

  const SectionTitle = ({ children }) => (
    <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
      // {children}
    </p>
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-950" data-testid="settings-page">
      <Header title="Ajustes" subtitle="// Configuración" />
      <div className="flex-1 overflow-y-auto no-scrollbar px-5 pt-5 pb-8">
        {/* Tema */}
        <div className="mb-6">
          <SectionTitle>Apariencia</SectionTitle>
          <div className="space-y-2.5">
            <Option
              active={theme === "light"}
              onClick={() => setTheme("light")}
              icon={Sun}
              title="Modo Claro"
              description="Por defecto · Taller iluminado"
              testid="theme-option-light"
            />
            <Option
              active={theme === "dark"}
              onClick={() => setTheme("dark")}
              icon={Moon}
              title="Modo Oscuro"
              description="Bajo brillo · Garaje nocturno"
              testid="theme-option-dark"
            />
          </div>
        </div>

        {/* Tamaño de letra */}
        <div className="mb-6">
          <SectionTitle>Tamaño de letra</SectionTitle>
          <div className="space-y-2.5">
            <Option
              active={fontSize === "normal"}
              onClick={() => setFontSize("normal")}
              icon={Type}
              title="Normal"
              description="Densidad estándar"
              testid="font-option-normal"
            />
            <Option
              active={fontSize === "large"}
              onClick={() => setFontSize("large")}
              icon={ALargeSmall}
              title="Muy Grande"
              description="Mejor legibilidad en taller"
              testid="font-option-large"
            />
          </div>
        </div>

        {/* Layout formulario */}
        <div className="mb-6">
          <SectionTitle>Tipo de checklist</SectionTitle>
          <div className="space-y-2.5">
            <Option
              active={formLayout === "single"}
              onClick={() => setFormLayout("single")}
              icon={ScrollText}
              title="Una página"
              description="Todas las preguntas a la vez"
              testid="layout-option-single"
            />
            <Option
              active={formLayout === "slides"}
              onClick={() => setFormLayout("slides")}
              icon={GalleryHorizontalEnd}
              title="Modo diapositiva"
              description="Pregunta a pregunta · Foco total"
              testid="layout-option-slides"
            />
          </div>
        </div>

        {/* Sesión */}
        <div className="mb-6">
          <SectionTitle>Sesión</SectionTitle>
          <div className="space-y-2.5">
            <button
              onClick={handleLogout}
              data-testid="settings-logout-btn"
              className="w-full flex items-center gap-3 p-4 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-red-500 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-sm bg-zinc-100 dark:bg-zinc-800 text-red-500 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-sm uppercase tracking-wider text-zinc-900 dark:text-white">
                  Cerrar sesión
                </p>
                <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
                  Salir del terminal
                </p>
              </div>
            </button>
            <button
              onClick={replayWizard}
              data-testid="settings-replay-wizard-btn"
              className="w-full flex items-center gap-3 p-4 rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-orange-500 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-sm bg-zinc-100 dark:bg-zinc-800 text-orange-500 flex items-center justify-center shrink-0">
                <RotateCcw className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-sm uppercase tracking-wider text-zinc-900 dark:text-white">
                  Repetir asistente inicial
                </p>
                <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
                  Volver a configurar paso a paso
                </p>
              </div>
            </button>
          </div>
        </div>

        {/* Sistema */}
        <div className="mb-6">
          <SectionTitle>Sistema</SectionTitle>
          <div className="rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 flex items-start gap-3">
            <Info className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
            <div className="font-mono-tech text-xs text-zinc-700 dark:text-zinc-300 space-y-1">
              <p>
                <span className="text-zinc-500">Versión:</span> Warranty Terminal v1.0
              </p>
              <p>
                <span className="text-zinc-500">Tema:</span>{" "}
                {theme === "dark" ? "Oscuro" : "Claro"}
              </p>
              <p>
                <span className="text-zinc-500">Tipografía:</span>{" "}
                {fontSize === "large" ? "Muy grande" : "Normal"}
              </p>
              <p>
                <span className="text-zinc-500">Checklist:</span>{" "}
                {formLayout === "slides" ? "Diapositiva" : "Una página"}
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate(-1)}
          data-testid="settings-back-btn"
          className="w-full flex items-center justify-center gap-2 h-12 rounded-sm bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-white font-mono-tech text-xs uppercase tracking-widest border border-zinc-200 dark:border-zinc-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
      </div>
    </div>
  );
}
